import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';
import moment, { DATE_FORMAT, getCurrentTimezone } from '@/utils/moment-wrapper.ts';
import momentTimeZone from 'moment-timezone';
import { i18n_t } from '@utils/i18n';
import { VanillaDateRange } from '@/data/vanilla-date-range';

export function normalizeTimeSeriesLabels (timeSeries) {
    if (!timeSeries?.config) return timeSeries;

    const dateRange = new VanillaDateRange(timeSeries.config);

    return {
        ...timeSeries,
        label: dateRange.toString(),
        spec: dateRange.timeSeries(),
    };
}

export const getDefaultShortcuts = () => [
    {
        label: i18n_t('timeSeriesPicker.shortcuts.Today'),
        type: 'Today',
        value: {
            start: 0,
            end: 0,
        },
    },
    {
        label: i18n_t('timeSeriesPicker.shortcuts.Yesterday'),
        type: 'Yesterday',
        value: {
            start: -1,
            end: -1,
        },
    },
    {
        label: i18n_t('timeSeriesPicker.shortcuts.Last 7 Days'),
        type: 'Last 7 Days',
        value: {
            start: -1,
            end: -7,
        },
    },
    {
        label: i18n_t('timeSeriesPicker.shortcuts.Last 30 Days'),
        type: 'Last 30 Days',
        value: {
            start: -1,
            end: -30,
        },
    },
    {
        label: i18n_t('timeSeriesPicker.shortcuts.Last 90 Days'),
        type: 'Last 90 Days',
        value: {
            start: -1,
            end: -90,
        },
    },
    {
        label: i18n_t('timeSeriesPicker.shortcuts.Last 180 Days'),
        type: 'Last 180 Days',
        value: {
            start: -1,
            end: -180,
        },
    },
    {
        label: i18n_t('timeSeriesPicker.shortcuts.Last 365 Days'),
        type: 'Last 365 Days',
        value: {
            start: -1,
            end: -365,
        },
    },
];

export function getSinceCreationDateRange (createdAt) {
    if (!createdAt) {
        return { start: 0, end: 0 };
    }

    const creationDay = moment(createdAt, { ignoreTimeZone: true }).startOf('day');
    const today = moment({ ignoreTimeZone: true }).startOf('day');
    const dayDiff = today.diff(creationDay, 'days');

    return { start: 0, end: dayDiff === 0 ? 0 : -dayDiff };
}

export function isCreatedDuringTodayPeriod (createdAt) {
    return getSinceCreationDateRange(createdAt).end === 0;
}

export function getSinceCreationShortcut ({ createdAt, label }) {
    return {
        label,
        type: 'Custom',
        value: getSinceCreationDateRange(createdAt),
    };
}

export function getTimeSeriesShortcutsWithSinceCreation ({
    createdAt,
    sinceCreationLabel,
    defaultShortcuts = getDefaultShortcuts(),
}) {
    if (isCreatedDuringTodayPeriod(createdAt)) {
        return defaultShortcuts;
    }

    return [getSinceCreationShortcut({ createdAt, label: sinceCreationLabel }), ...defaultShortcuts];
}

/**
 * Preset list for AI Agent experiment / comparison date pickers (dropdown shortcuts only).
 * Types must match {@link formatDates} switch cases.
 */
export const getExperimentComparisonDateShortcuts = () => [
    {
        label: i18n_t('aiAgent.experimentSetup.Last 7 days'),
        type: 'Last 7 Days',
        value: { start: -1, end: -7 },
    },
    {
        label: i18n_t('aiAgent.experimentSetup.Last 30 days'),
        type: 'Last 30 Days',
        value: { start: -1, end: -30 },
    },
    {
        label: i18n_t('aiAgent.experimentSetup.Last 90 days'),
        type: 'Last 90 Days',
        value: { start: -1, end: -90 },
    },
    {
        label: i18n_t('aiAgent.experimentSetup.Next 7 days'),
        type: 'Next 7 Days',
        value: { start: 0, end: 6 },
    },
    {
        label: i18n_t('aiAgent.experimentSetup.Next 30 days'),
        type: 'Next 30 Days',
        value: { start: 0, end: 29 },
    },
    {
        label: i18n_t('aiAgent.experimentSetup.Next 90 days'),
        type: 'Next 90 Days',
        value: { start: 0, end: 89 },
    },
];

/**
 * Convertes a timeSeries config object into a { start, end } pair of JS Date objects.
 */
export function formatDates ({ start, end, type } = {}, addDayToEnd = false, includeType = false) {
    const today = moment(undefined, { ignoreTimeZone: true })
        .startOf('day')
        .toDate();

    const yesterday = moment(undefined, { ignoreTimeZone: true })
        .subtract(1, 'days')
        .startOf('day')
        .toDate();

    switch (type) {
        case 'Today': {
            return {
                start: today,
                end: today,
                ...(includeType && { type }),
            };
        }
        case 'Yesterday': {
            return {
                start: yesterday,
                end: yesterday,
                ...(includeType && { type }),
            };
        }
        case 'Last 7 Days': {
            return {
                start: moment(undefined, { ignoreTimeZone: true })
                    .startOf('day')
                    .subtract(7, 'days')
                    .toDate(),
                end: addDayToEnd ? today : yesterday,
                ...(includeType && { type }),
            };
        }
        case 'Last 14 Days': {
            return {
                start: moment(undefined, { ignoreTimeZone: true })
                    .startOf('day')
                    .subtract(14, 'days')
                    .toDate(),
                end: addDayToEnd ? today : yesterday,
                ...(includeType && { type }),
            };
        }
        case 'Last 30 Days': {
            return {
                start: moment(undefined, { ignoreTimeZone: true })
                    .startOf('day')
                    .subtract(30, 'days')
                    .toDate(),
                end: addDayToEnd ? today : yesterday,
                ...(includeType && { type }),
            };
        }
        case 'Last 90 Days': {
            return {
                start: moment(undefined, { ignoreTimeZone: true })
                    .startOf('day')
                    .subtract(90, 'days')
                    .toDate(),
                end: addDayToEnd ? today : yesterday,
                ...(includeType && { type }),
            };
        }
        case 'Last 180 Days': {
            return {
                start: moment(undefined, { ignoreTimeZone: true })
                    .startOf('day')
                    .subtract(180, 'days')
                    .toDate(),
                end: addDayToEnd ? today : yesterday,
                ...(includeType && { type }),
            };
        }
        case 'Last 365 Days': {
            return {
                start: moment(undefined, { ignoreTimeZone: true })
                    .startOf('day')
                    .subtract(365, 'days')
                    .toDate(),
                end: addDayToEnd ? today : yesterday,
                ...(includeType && { type }),
            };
        }
        case 'Last 7 Days (and today)': {
            return {
                start: moment(undefined, { ignoreTimeZone: true })
                    .startOf('day')
                    .subtract(7, 'days')
                    .toDate(),
                end: today,
                ...(includeType && { type }),
            };
        }
        case 'Last 30 Days (and today)': {
            return {
                start: moment(undefined, { ignoreTimeZone: true })
                    .startOf('day')
                    .subtract(30, 'days')
                    .toDate(),
                end: today,
                ...(includeType && { type }),
            };
        }
        case 'Next 7 Days': {
            return {
                start: today,
                end: moment(undefined, { ignoreTimeZone: true })
                    .startOf('day')
                    .add(6, 'days')
                    .toDate(),
                ...(includeType && { type }),
            };
        }
        case 'Next 30 Days': {
            return {
                start: today,
                end: moment(undefined, { ignoreTimeZone: true })
                    .startOf('day')
                    .add(29, 'days')
                    .toDate(),
                ...(includeType && { type }),
            };
        }
        case 'Next 90 Days': {
            return {
                start: today,
                end: moment(undefined, { ignoreTimeZone: true })
                    .startOf('day')
                    .add(89, 'days')
                    .toDate(),
                ...(includeType && { type }),
            };
        }
        default:
        case 'Custom': {
            // Legacy user settings may have stored start/end as numeric Unix timestamps rather than
            // ISO date strings. Numeric values are interpreted in subscription timezone, falling back
            // to UTC when none is set (matching backend date boundary behavior); string values use
            // browser-local time as before.
            const parseDateValue = (value) => {
                if (typeof value === 'number') {
                    const timezone = getCurrentTimezone() || 'UTC';

                    return momentTimeZone.tz(value, timezone);
                }

                return moment(value, { ignoreTimeZone: true });
            };

            return {
                start: parseDateValue(start).startOf('day').toDate(),
                end: parseDateValue(end)
                    .startOf('day')
                    .subtract(addDayToEnd ? 0 : 1, 'days')
                    .toDate(),
                ...(includeType && { type }),
            };
        }
    }
}

export const getFirstLastDateValues = (type, period = 'dayRange') => {
    const lastRegex = /Last (\d+) Day(s)?/;
    if (type && lastRegex.test(type)) {
        const parts = type.match(lastRegex);
        if (parts.length > 1) {
            const numDays = parts[1];

            return {
                first: `dateAdd(now(),-${numDays},"days")`,
                last: 'dateAdd(now(),-1,"days")',
                period,
            };
        }
    }

    return undefined;
};

/**
 * @deprecated - timeSeries in aggregations should be built using the date() fn.
 * ex. first: date("2025-01-02")
 * or first: dateAdd(now(),-30,"days")
 *
 * Convert a { start, end } pair of JS Date objects to a full-fledged time series (with config, spec, and label fields).
 * @param {Boolean} [specFirstInMS=false] Response returns with spec.first in "YYYY-MM-DD" format by default. Setting true returns with prop in MS.
 */
export function createTimeSeriesFromDate (date, specFirstInMS = false, includeType = false) {
    const start = moment(date.start, { ignoreTimeZone: true });
    const end = moment(date.end, { ignoreTimeZone: true });

    let type;
    if (includeType) {
        if (date.type) {
            type = date.type;
        } else {
            const startDiff = start.diff(moment(undefined, { ignoreTimeZone: true }), 'days');
            const endDiff = end.diff(moment(undefined, { ignoreTimeZone: true }), 'days');
            // Note: With this implementation,
            // a date range that has the same number of days as a custom range
            // will display a label like a relative time range.
            // For example, a manual range of 10/1/2024 - 10/30/2024
            // will display a label like "Last 30 Days" if the current date is 10/31/2024.
            // This strategy may not be desirable long-term, but it is the current behavior.
            type = getDefaultShortcuts().find(
                (shortcut) => shortcut.value.end === startDiff && shortcut.value.start === endDiff,
            )?.type;
        }
    }

    return {
        config: {
            ...(type && { type }),
            start: start.format(DATE_FORMAT.iso),
            end: moment(date.end, { ignoreTimeZone: true })
                .add(1, 'days')
                .format(DATE_FORMAT.iso),
        },
        spec: {
            first: specFirstInMS ? start.valueOf() : start.format(DATE_FORMAT.iso),
            period: 'dayRange',
            count: end.diff(start, 'days') + 1, // include start
        },
        label: type || formatStartEndDates(start, end),
    };
}

/**
 * @param {Boolean} [specFirstInMS=false] Response returns with spec.first in "YYYY-MM-DD" format by default. Setting true returns with prop in MS.
 */
export function convertConfigToTimeSeries (config, specFirstInMS = false, includeType = false) {
    const series = { config: cloneDeep(config) };
    merge(series, createTimeSeriesFromDate(formatDates(config), specFirstInMS, includeType));

    return series;
}

export const removeTimeSeriesPickerExtraDay = (timeSeries) => {
    const clonedTimeSeries = cloneDeep(timeSeries);

    if (clonedTimeSeries.config.type === 'Custom') {
        clonedTimeSeries.config.end = moment(clonedTimeSeries.config.end, {
            ignoreTimeZone: true,
        })
            .subtract(1, 'days')
            .format('YYYY-MM-DD');
        if (clonedTimeSeries.spec && clonedTimeSeries.spec.count) {
            clonedTimeSeries.spec.count -= 1;
        }
    }

    return clonedTimeSeries;
};

export const addTimeSeriesPickerExtraDay = (timeSeries) => {
    const clonedTimeSeries = cloneDeep(timeSeries);

    if (clonedTimeSeries.config.type === 'Custom') {
        clonedTimeSeries.config.end = moment(clonedTimeSeries.config.end, {
            ignoreTimeZone: true,
        })
            .add(1, 'days')
            .format('YYYY-MM-DD');
        if (clonedTimeSeries.spec && clonedTimeSeries.spec.count) {
            clonedTimeSeries.spec.count += 1;
        }
    }

    return clonedTimeSeries;
};

/**
 * @param {Object} start Moment start date
 * @param {Object} end Moment end date
 * @param {boolean} [useDaySuffix=false] Returns dates with suffix included in day value (ex. Oct 23rd instead of Oct 23). Defaults to false.
 * @param {boolean} [alwaysIncludeYear=false] By default, start does not include year if in same year as end. Set to true to always show full dates
 * @returns {string} isSameDay ? 'MMM D, YYYY' : 'start - end'
 */
export function formatStartEndDates (start, end, useDaySuffix = false, alwaysIncludeYear = false) {
    if (start) start = moment(start, { ignoreTimeZone: true });
    if (end) end = moment(end, { ignoreTimeZone: true });

    const getMonthDayFormat = (withYear) =>
        `${useDaySuffix ? 'MMM Do' : 'MMM D'}${withYear || alwaysIncludeYear ? ', YYYY' : ''}`;

    if (!end || start.isSame(end, 'day')) {
        return `${start.format(getMonthDayFormat(true))}`;
    }

    const isSameYear = start.isSame(end, 'year');

    return `${start.format(getMonthDayFormat(!isSameYear))} - ${end.format(getMonthDayFormat(true))}`;
}
